import type { Branding } from '@helios/ui';
import type { Claims, AppPolicy } from '@helios/authz';

interface NoAccessScreenProps {
  branding: Branding;
  onSignOut: () => void;
  claims: Claims | null;
  appPolicy: AppPolicy;
}

export function NoAccessScreen({ branding, onSignOut, claims, appPolicy }: NoAccessScreenProps) {
  const primaryColor = branding.theme?.primaryColor || '#2563eb';

  // Debug info
  const activeOrg = claims?.activeOrg;
  const userRoles = activeOrg && claims?.orgs?.[activeOrg]?.roles;
  const requiredRoles = appPolicy.entryAnyOfRoles;

  // Log to console for debugging
  console.log('[NoAccessScreen] Access denied:', {
    appId: appPolicy.appId,
    requiredRoles,
    activeOrg,
    userRoles,
    fullClaims: claims,
  });

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl p-8">
        {/* Logo */}
        {branding.orgLogoUrl && (
          <img src={branding.orgLogoUrl} alt={branding.orgName} className="h-16 mx-auto mb-6" />
        )}

        <div className="text-center mb-6">
          {/* Icon */}
          <div className="mb-6">
            <div
              className="w-20 h-20 rounded-full mx-auto flex items-center justify-center"
              style={{ backgroundColor: `${primaryColor}20` }}
            >
              <svg
                className="w-10 h-10"
                style={{ color: primaryColor }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
          </div>

          {/* Message */}
          <h1 className="text-2xl font-bold text-slate-900 mb-3">Access Restricted</h1>
          <p className="text-slate-600 mb-6">
            You don't have permission to access this application. Please contact your administrator
            if you believe this is an error.
          </p>
        </div>

        {/* Debug Info */}
        <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left">
          <h2 className="font-semibold text-slate-900 mb-3 text-sm">Access Requirements</h2>
          <dl className="space-y-2 text-sm">
            <div>
              <dt className="text-slate-500 font-medium">App ID:</dt>
              <dd className="text-slate-900 font-mono">{appPolicy.appId}</dd>
            </div>
            <div>
              <dt className="text-slate-500 font-medium">Required Roles (any of):</dt>
              <dd className="text-slate-900">
                <div className="flex gap-1 flex-wrap mt-1">
                  {requiredRoles.map((role) => (
                    <span
                      key={role}
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800"
                    >
                      {role}
                    </span>
                  ))}
                </div>
              </dd>
            </div>
            <div>
              <dt className="text-slate-500 font-medium">Your Active Org:</dt>
              <dd className="text-slate-900 font-mono">{activeOrg || '(none)'}</dd>
            </div>
            <div>
              <dt className="text-slate-500 font-medium">Your Roles in Active Org:</dt>
              <dd className="text-slate-900">
                {userRoles && userRoles.length > 0 ? (
                  <div className="flex gap-1 flex-wrap mt-1">
                    {userRoles.map((role) => (
                      <span
                        key={role}
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800"
                      >
                        {role}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-slate-400 italic">(no roles)</span>
                )}
              </dd>
            </div>
          </dl>
        </div>

        {/* Raw Claims */}
        <details className="bg-slate-50 rounded-xl p-4 mb-6 text-left">
          <summary className="text-sm font-semibold text-slate-900 cursor-pointer hover:text-slate-700">
            View Raw Claims (Debug)
          </summary>
          <pre className="mt-3 p-3 bg-white rounded-lg text-xs overflow-x-auto font-mono text-slate-700">
            {claims ? JSON.stringify(claims, null, 2) : 'No claims'}
          </pre>
        </details>

        {/* Support Info */}
        {(branding.supportEmail || branding.supportPhone) && (
          <div className="bg-blue-50 rounded-xl p-4 mb-6 text-sm text-slate-700 text-center">
            <p className="font-medium mb-2">Need help?</p>
            {branding.supportEmail && (
              <p>
                Email:{' '}
                <a href={`mailto:${branding.supportEmail}`} className="underline">
                  {branding.supportEmail}
                </a>
              </p>
            )}
            {branding.supportPhone && <p>Phone: {branding.supportPhone}</p>}
          </div>
        )}

        {/* Sign Out Button */}
        <button
          onClick={onSignOut}
          className="w-full py-2.5 px-4 text-sm font-semibold text-white rounded-xl shadow-sm hover:shadow-md transition-all"
          style={{ backgroundColor: primaryColor }}
        >
          Sign Out
        </button>
      </div>
    </div>
  );
}
